import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import api from '../utils/api';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { AlertTriangle, Layers, Merge, RefreshCcw, ChevronDown, ChevronUp } from 'lucide-react';

/**
 * Admin diagnostic page — lists every service name that has more than one
 * `service_id` and lets the admin merge the stale ids into a canonical one.
 * The Merge button reassigns every appointment / package item / consumption /
 * payment / follow-up reference before deactivating the stale service records.
 */
const ServicesAuditPage = ({ user, onLogout }) => {
  const [loading, setLoading] = useState(false);
  const [merging, setMerging] = useState(null); // canonical_id currently being merged
  const [groups, setGroups] = useState([]);
  const [expanded, setExpanded] = useState({}); // { name_lower: bool }
  const [canonicalChoice, setCanonicalChoice] = useState({}); // { name_lower: canonical_id }

  const load = async () => {
    try {
      setLoading(true);
      const res = await api.get('/services/duplicates');
      const rows = res.data?.groups || [];
      setGroups(rows);
      // Default canonical = first row (highest open+total by the backend sort)
      const initial = {};
      rows.forEach(g => {
        if (g.duplicate_ids?.[0]) initial[g.name_lower] = g.duplicate_ids[0].service_id;
      });
      setCanonicalChoice(initial);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load audit');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const runMerge = async (group) => {
    const canonical_id = canonicalChoice[group.name_lower];
    if (!canonical_id) { toast.error('Pick a canonical id'); return; }
    const stale_ids = (group.duplicate_ids || [])
      .map(r => r.service_id)
      .filter(id => id !== canonical_id);
    if (stale_ids.length === 0) { toast.error('Nothing to merge'); return; }

    const totalRefs = group.duplicate_ids
      .filter(r => r.service_id !== canonical_id)
      .reduce((acc, r) => acc + r.appointments_total + r.packages_referencing, 0);

    const ok = window.confirm(
      `Merge ${stale_ids.length} duplicate id(s) of "${group.display_name}" into ${canonical_id.slice(0, 8)}…?\n\n` +
      `This will reassign ~${totalRefs} references and deactivate the stale service records.\n\nContinue?`
    );
    if (!ok) return;

    try {
      setMerging(group.name_lower);
      const res = await api.post('/services/merge', { canonical_id, stale_ids });
      const r = res.data?.reassigned || {};
      toast.success(
        `Merged "${group.display_name}" · ` +
        `${r.appointments || 0} appts · ${r.package_items || 0} pkg items · ` +
        `${r.package_consumptions || 0} redemptions · ${r.payments || 0} payments`,
        { duration: 6000 }
      );
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Merge failed');
    } finally {
      setMerging(null);
    }
  };

  const toggle = (n) => setExpanded(prev => ({ ...prev, [n]: !prev[n] }));

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="max-w-5xl mx-auto p-4 sm:p-6 space-y-4">
        {/* Header */}
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 flex items-center gap-2">
              <Layers className="w-6 h-6 text-purple-600" />
              Services Audit
            </h1>
            <p className="text-sm text-slate-500 mt-1 max-w-2xl">
              Every service name here has multiple <code className="text-xs bg-slate-100 px-1 rounded">service_id</code> records.
              This is why some package redemptions fail — the appointment is on one id, the package item on another.
              Merge each group into a single canonical id to unify the references.
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={load}
            disabled={loading}
            data-testid="services-audit-refresh"
          >
            <RefreshCcw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>

        {/* Empty state */}
        {!loading && groups.length === 0 && (
          <div className="rounded-xl border-2 border-dashed border-emerald-200 bg-emerald-50 px-6 py-10 text-center">
            <div className="text-emerald-700 font-semibold text-lg">All clean 🎉</div>
            <div className="text-sm text-emerald-600 mt-1">No duplicate service records found.</div>
          </div>
        )}

        {/* Groups */}
        {groups.map(g => {
          const canonId = canonicalChoice[g.name_lower];
          const isOpen = !!expanded[g.name_lower];
          const staleCount = (g.duplicate_ids || []).length - 1;
          const openRefs = g.duplicate_ids.reduce((a, r) => a + r.appointments_open_unpaid, 0);
          return (
            <div
              key={g.name_lower}
              className="rounded-xl border border-amber-200 bg-white overflow-hidden shadow-sm"
              data-testid={`services-audit-group-${g.name_lower.replace(/\s+/g, '-')}`}
            >
              <div className="flex items-center justify-between px-4 py-3 bg-amber-50 border-b border-amber-100">
                <div className="flex items-center gap-3 min-w-0">
                  <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-900 truncate">{g.display_name}</div>
                    <div className="text-xs text-slate-500">
                      {g.duplicate_ids.length} duplicate ids · {openRefs} open unpaid appointments across them
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button
                    size="sm"
                    onClick={() => runMerge(g)}
                    disabled={merging === g.name_lower || staleCount === 0}
                    className="bg-purple-600 hover:bg-purple-700 h-8 text-xs"
                    data-testid={`services-audit-merge-${g.name_lower.replace(/\s+/g, '-')}`}
                  >
                    <Merge className="w-3.5 h-3.5 mr-1.5" />
                    {merging === g.name_lower ? 'Merging…' : `Merge ${staleCount} into 1`}
                  </Button>
                  <button
                    onClick={() => toggle(g.name_lower)}
                    className="p-1 hover:bg-amber-100 rounded"
                    data-testid={`services-audit-toggle-${g.name_lower.replace(/\s+/g, '-')}`}
                  >
                    {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {isOpen && (
                <div className="divide-y divide-slate-100">
                  {g.duplicate_ids.map(row => {
                    const isCanon = row.service_id === canonId;
                    return (
                      <label
                        key={row.service_id}
                        className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer transition ${
                          isCanon ? 'bg-emerald-50' : 'hover:bg-slate-50'
                        }`}
                      >
                        <input
                          type="radio"
                          name={`canon-${g.name_lower}`}
                          checked={isCanon}
                          onChange={() => setCanonicalChoice(prev => ({ ...prev, [g.name_lower]: row.service_id }))}
                          className="accent-purple-600"
                          data-testid={`services-audit-radio-${row.service_id}`}
                        />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <code className="text-xs font-mono text-slate-700">{row.service_id}</code>
                            {isCanon && (
                              <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded">
                                KEEP
                              </span>
                            )}
                            {!row.active && (
                              <span className="text-[10px] font-semibold text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">
                                inactive
                              </span>
                            )}
                          </div>
                          <div className="text-[11px] text-slate-500 mt-0.5">
                            {row.appointments_total} appts total · {row.appointments_open_unpaid} open unpaid ·
                            &nbsp;{row.packages_referencing} pkg items · created {String(row.created_at || '').slice(0, 10)}
                          </div>
                        </div>
                      </label>
                    );
                  })}
                  <div className="px-4 py-2 text-[11px] text-slate-500 bg-slate-50">
                    Tick the row you want to <b>keep</b>. All others become stale and their references get moved to your pick.
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Layout>
  );
};

export default ServicesAuditPage;
